import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Switch } from '../../../components/Switch/Switch';
import { SurveyQuestion } from '../../../types/survey';
import { SURVEY_QUESTION_TEXT_MAX_LENGTH, SURVEY_RATING_MAX, SURVEY_RATING_MIN } from '../surveyUtils';

interface Props {
  question: SurveyQuestion;
  /** Number the employee sees: 1..n for text questions. */
  position: number;
  locales: string[];
  canWrite: boolean;
  onTextChange: (locale: string, text: string) => void;
  onRequiredChange: (required: boolean) => void;
  onRemove: () => void;
  /** D-A16: translates the main-language text into `locale`. */
  onTranslate: (locale: string) => void;
  /** Locale being translated now, if any. */
  translatingLocale?: string;
}

const TEXT_PLACEHOLDER = 'Ej: ¿Qué es lo que más te gusta del centro?';
const RATING_PLACEHOLDER = 'Ej: ¿Cómo valorarías tu experiencia?';

export function SurveyQuestionCard({
  question,
  position,
  locales,
  canWrite,
  onTextChange,
  onRequiredChange,
  onRemove,
  onTranslate,
  translatingLocale,
}: Props): React.ReactElement {
  const isRating = question.type === 'rating';
  const title = isRating ? 'Valoración' : `Pregunta ${position}`;
  const showLocales = locales.length > 1;
  const mainLocale = locales[0];
  const hasMainText = !!question.texts[mainLocale]?.trim();
  // D-A13: an empty text is an error only once the employee has left the field or edited it, not on first open
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (locale: string): void => setTouched((current) => (current[locale] ? current : { ...current, [locale]: true }));

  return (
    <article className='survey-question-card' data-slot={question.slot}>
      <header className='survey-question-card-header'>
        <span className='survey-question-card-badge' aria-hidden>
          {isRating ? <i className='pi pi-star' /> : position}
        </span>
        <div className='survey-question-card-title'>
          <h3>{title}</h3>
          <span>{isRating ? `Escala del ${SURVEY_RATING_MIN} al ${SURVEY_RATING_MAX}` : 'Respuesta libre'}</span>
        </div>
        {canWrite && (
          <Button
            type='button'
            icon='pi pi-trash'
            rounded
            text
            severity='secondary'
            aria-label={`Quitar ${title.toLowerCase()}`}
            tooltip='Quitar'
            tooltipOptions={{ position: 'left' }}
            onClick={onRemove}
          />
        )}
      </header>

      <div className='survey-question-card-fields'>
        {locales.map((locale) => {
          const inputId = `survey-question-${question.slot}-${locale}`;
          const value = question.texts[locale] ?? '';
          const isInvalid = canWrite && !!touched[locale] && value.trim() === '';
          return (
            <div key={locale} className='survey-question-card-field'>
              {showLocales && (
                <label htmlFor={inputId} className='survey-question-card-locale'>
                  {locale.toUpperCase()}
                </label>
              )}
              <InputText
                id={inputId}
                value={value}
                placeholder={isRating ? RATING_PLACEHOLDER : TEXT_PLACEHOLDER}
                maxLength={SURVEY_QUESTION_TEXT_MAX_LENGTH}
                disabled={!canWrite}
                aria-label={showLocales ? undefined : `Texto de ${title.toLowerCase()}`}
                invalid={isInvalid}
                aria-invalid={isInvalid}
                onBlur={() => touch(locale)}
                onChange={(event) => {
                  touch(locale);
                  onTextChange(locale, event.target.value);
                }}
              />
              {canWrite && showLocales && locale === mainLocale && (
                // Same width as the button of the other languages, so the fields line up
                <span className='survey-question-card-translate-spacer' aria-hidden />
              )}
              {canWrite && locale !== mainLocale && (
                <Button
                  type='button'
                  icon='pi pi-language'
                  rounded
                  text
                  severity='secondary'
                  className='survey-question-card-translate'
                  aria-label={`Traducir ${isRating ? 'la valoración' : `la pregunta ${position}`} al ${locale.toUpperCase()}`}
                  tooltip='Traducir'
                  tooltipOptions={{ position: 'top' }}
                  loading={translatingLocale === locale}
                  disabled={!hasMainText || !!translatingLocale}
                  onClick={() => onTranslate(locale)}
                />
              )}
            </div>
          );
        })}
      </div>

      <footer className='survey-question-card-footer'>
        <Switch
          id={`survey-required-${question.slot}`}
          label='Obligatoria'
          primeSwitchProps={{
            checked: question.required,
            disabled: !canWrite,
            onChange: (event) => onRequiredChange(!!event.value),
          }}
        />
      </footer>
    </article>
  );
}
